import { useEffect, useState, useRef } from 'react'

interface Metric {
  value: number
  label: string
  suffix?: string
  prefix?: string
}

const CountUpMetrics = () => {
  const [isVisible, setIsVisible] = useState(false)
  const sectionRef = useRef<HTMLDivElement>(null)

  const metrics: Metric[] = [
    {
      value: 950000,
      label: 'kW low carbon installations',
      suffix: '+'
    },
    {
      value: 70,
      label: 'of our work comes from customer referrals',
      suffix: '%'
    },
    {
      value: 100,
      label: 'Customer Satisfaction',
      suffix: '%'
    }
  ]

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !isVisible) {
            setIsVisible(true)
          }
        })
      },
      { threshold: 0.3 }
    )

    const currentRef = sectionRef.current
    if (currentRef) {
      observer.observe(currentRef)
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef)
      }
    }
  }, [isVisible])

  return (
    <section ref={sectionRef} className="py-12 bg-gray-50 border-y border-gray-200">
      <div className="container-custom">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {metrics.map((metric, index) => (
            <MetricCard
              key={index}
              metric={metric}
              isVisible={isVisible}
              delay={index * 100}
            />
          ))}
        </div>
      </div>
    </section>
  )
}

interface MetricCardProps {
  metric: Metric
  isVisible: boolean
  delay: number
}

const MetricCard = ({ metric, isVisible, delay }: MetricCardProps) => {
  const [count, setCount] = useState(0)

  useEffect(() => {
    if (!isVisible) return

    const timer = setTimeout(() => {
      const duration = 2000 // 2 seconds
      const steps = 60
      const increment = metric.value / steps
      const stepDuration = duration / steps

      let currentStep = 0

      const interval = setInterval(() => {
        currentStep++
        const newValue = Math.min(
          Math.round(increment * currentStep),
          metric.value
        )
        setCount(newValue)

        if (currentStep >= steps) {
          clearInterval(interval)
        }
      }, stepDuration)

      return () => clearInterval(interval)
    }, delay)

    return () => clearTimeout(timer)
  }, [isVisible, metric.value, delay])

  return (
    <div className="text-center">
      <div className="text-4xl md:text-5xl font-bold text-primary-green mb-2">
        {metric.prefix}
        {count.toLocaleString()}
        {metric.suffix}
      </div>
      <div className="text-gray-600 font-medium">{metric.label}</div>
    </div>
  )
}

export default CountUpMetrics
